import { Box, Container, Stack, Typography } from '@mui/material'
import { TriangleAlert } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

import { IconBadge } from 'components/UI/IconBadge'
import { buildScanErrorReportMailto, ERROR_REPORT_EMAIL } from 'constants/support'

/**
 * Where an UNEXPECTED server error during `GET /verify/:global_tag_id` lands.
 *
 * The fourth sibling of `pages/TagReplaced`, `pages/TagNotActive` and
 * `pages/CertificateNotActive` — read those files' headers first, this one borrows their
 * layout and most of their rules. Where they cover KNOWN outcomes of a tap (retired tag,
 * held tag, unissued certificate), this one covers the residual: the global exception
 * filter caught something nobody anticipated.
 *
 * WHY IT EXISTS AT ALL. Until now that fall-through answered JSON, so a real customer who
 * tapped a luxury-goods tag got `{"message":"Something went wrong on our end", ...}` rendered
 * in their phone browser — and on iOS Safari not even rendered, but offered as a FILE
 * DOWNLOAD named after the tag id (`041F67A2001990.json`). Photographed in the wild. That is
 * the worst thing this product can show: the person is holding a physical object and the
 * screen is behaving like malware.
 *
 * WHY THE COPY LEADS WITH "NOT YOUR ITEM" (load-bearing, not padding). Every other page in
 * this family is read by someone deciding whether the thing in their hand is real. A failure
 * with no explanation reads as an accusation by default — the natural inference from "the
 * authenticity check didn't work" is "the item failed the authenticity check". So the page
 * has to say, in plain words and early, that nothing is wrong with the item. That sentence is
 * the entire reason this page is not a generic 500.
 *
 * ⚠️ TWO PLACES THIS DELIBERATELY BREAKS THE SIBLINGS' DOCTRINE. Do not "fix" either for
 * consistency without reading the reasoning on `ERROR_REPORT_EMAIL` in `constants/support`.
 *
 *   1. IT NAMES VERA, and sends the customer to Vera's support address. The siblings carry
 *      no Vera identity and point at the brand, on the documented grounds that "the visitor
 *      believes they are dealing with the brand, and the brand is who can actually look the
 *      item up". That is right for those pages, because they are about the ITEM's state,
 *      which is genuinely the brand's business. This page is about VERA BEING BROKEN. The
 *      routing rule is "whoever can act on it", and a 500 in Vera's own verify endpoint is
 *      something the brand's desk cannot reproduce, cannot fix, does not own the code for,
 *      and will forward to Vera anyway. `components/UI/ErrorBoundary` made exactly this call
 *      for a render crash; this is the same call for a server one.
 *   2. IT INVITES A RETRY. The siblings describe settled states where tapping again changes
 *      nothing, so offering it would be a lie. Here the error is by definition unexplained
 *      and may well be transient, so "try tapping it again" is the most likely fix and is the
 *      first thing offered.
 *
 * ⚠️ THE RETRY IS AN INSTRUCTION, NOT A BUTTON, and that is forced rather than chosen. A
 * button would need the URL to re-request, and this page is never given it: the contract with
 * the API is `?ref=` and nothing else, and by the time the redirect lands the original
 * `/verify/:global_tag_id` is gone (`document.referrer` is not dependable across the redirect
 * and is not part of any contract). The physical tap is the only retry available, so the copy
 * asks for the physical tap. If the API is ever amended to pass the verify URL back, a real
 * "Try again" button becomes possible and should be the primary action.
 *
 * `?ref=` is an optional correlation id the API passes when it has one, so a customer can
 * quote it and it can be found in the server logs. It is rendered ONLY when present — never
 * an empty "Reference:" label, which would look like a second thing that failed — and it is
 * folded into the support mailto (subject AND body) so nobody has to read it out or retype
 * it off a phone screen. Nothing on the page depends on it: the API is free not to supply
 * one, and the support link works either way.
 *
 * ⚠️ Registering the route in `App.tsx` is only half the wiring — `/something-went-wrong`
 * must also be in STANDALONE_ENTRY_PATHS in `index.tsx`, or MemoryRouter ignores the path,
 * boots at '/', and bounces the visitor to SIGN-IN. That failure is invisible to typecheck
 * and to code review (the route registration looks correct in isolation) and has now caught
 * three of this page's siblings; see the comment above that list.
 */
export default function SomethingWentWrong() {
  const [searchParams] = useSearchParams()

  // Trimmed, because an empty-string `?ref=` must be treated as absent — the rule is
  // "render nothing extra when there is no id", and `''` is no id.
  const ref = searchParams.get('ref')?.trim()

  return (
    <Container maxWidth='sm'>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
          textAlign: 'center',
        }}
      >
        <Stack spacing={3} alignItems='center'>
          {/*
            The shared `IconBadge`, the same object every state screen in this
            app now opens with. A triangle in the ALERT tone, matching both
            error boundaries, because this page and those two say the same
            thing: Vera broke. The sibling pages in this family carry
            state-specific glyphs in the brand tone instead, since they are
            describing a condition rather than reporting a fault.

            Outer spacing is 3 (24px on this app's 8px base — NOT the 4px base
            `@repo/ui` and `apps/platform` use, §29) so the badge sits 24px
            above the title, matching `ErrorBoundary` and `SuccessState`. The
            copy keeps its own 16px rhythm in the nested Stack below.
          */}
          <IconBadge icon={TriangleAlert} tone='alert' />

          <Stack spacing={2} alignItems='center'>
            <Typography variant='h4' component='h1'>
              Something went wrong
            </Typography>

            {/*
              Separate sentences rather than clauses joined by em-dashes (§15). lineHeight 1.5
              so the copy does not read dense on a phone, which is the only device this page is
              ever seen on.
            */}
            <Typography variant='body1' color='text.secondary' sx={{ lineHeight: 1.5 }}>
              This is a problem on our end. There is nothing wrong with your item. Please try
              tapping it again in a moment.
            </Typography>

            {/*
              A text link rather than a Button, matching `ErrorBoundary`'s handling of the same
              decision: writing an email is the fallback for when the retry above has failed,
              and giving it a button's weight would compete with the action we actually want
              first. Here there is no primary button at all (see the header on why the retry is
              an instruction), so a link is also the only weight that does not overstate this as
              the expected next step.

              `mailto:` because there is NO in-app support route in this app that accepts
              context — `pages/Help` is an article and glossary reader with no contact surface,
              and every existing support affordance (ErrorBoundary, Signup, SignupSuccess,
              SealOpenedDrawer) is a mailto. Building an intake here would be a second door to
              maintain for the sake of one page.
            */}
            <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
              If it keeps happening,{' '}
              <Box
                component='a'
                href={buildScanErrorReportMailto(ref)}
                sx={{ color: 'inherit', textDecoration: 'underline' }}
              >
                email {ERROR_REPORT_EMAIL}
              </Box>
              {/* No trailing fragment when there is no id — the sentence has to end cleanly
                  either way, not on a dangling space. */}
              {ref ? '. Your reference code is already in the email.' : '.'}
            </Typography>

            {ref ? (
              /*
                Shown as well as being carried in the mailto, because the two serve different
                readers: the link covers the customer who taps it, and the visible code covers
                the one who phones, forwards a screenshot, or is standing in a shop telling
                someone. `wordBreak` because the id is opaque and may not break at a sensible
                point on a 375px screen, and horizontal overflow on a phone is worse than an
                ugly wrap.
              */
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ lineHeight: 1.5, wordBreak: 'break-all' }}
              >
                Reference: {ref}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    </Container>
  )
}
